import React, { InputHTMLAttributes, memo, useCallback, useMemo } from 'react';
import { ButtonThemes } from '../../libraries/ButtonTheme';
import { FileTypes, ImageTypes } from '../../libraries/FileTypes';
import { getMergedClassNames, getUniqueKey } from '../../utils/HTMLutils';
import Button, { ButtonProps } from '../button/Button';
import './ButtonFileUpload.scss';
const rootClassName = 'component-button-file-upload';

export type ButtonFileUploadProps = {
  label?: string;
  leftIcon?: React.ReactNode;
  theme?: ButtonThemes;
  allowedFileTypes?: Array<FileTypes | string>;
  disabled?: boolean;
  onTypeNotAllowed?: () => void;
  buttonProps?: Omit<
    ButtonProps,
    'theme' | 'onClick' | 'disabled' | 'leftIcon' | 'w100' | 'children'
  > & { 'data-testid'?: string };
  w100?: boolean;
  inputProps?: Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'onChange' | 'multiple' | 'tabIndex' | 'type' | 'accept' | 'hidden'
  > & { 'data-testid'?: string };
} & (
  | {
      multiple: true;
      value?: File[];
      onChange?: (value: File[]) => void;
    }
  | {
      multiple?: false;
      value?: File;
      onChange?: (value?: File) => void;
    }
);

function ButtonFileUpload(props: ButtonFileUploadProps) {
  const id = useMemo(getUniqueKey, []);

  const isAllowedFileType = useCallback(
    (fileName: string) => {
      const extension = fileName.replace(/^(.)*(\..+)$/, '$2').toLowerCase();
      if (props.allowedFileTypes && props.allowedFileTypes.length) {
        if (props.allowedFileTypes.indexOf(extension as FileTypes) > -1) {
          return true;
        } else if (
          props.allowedFileTypes.indexOf(FileTypes.ALL_IMAGES) > -1 &&
          Object.values(ImageTypes).indexOf(extension as ImageTypes) > -1
        ) {
          return true;
        } else {
          return false;
        }
      }
      return true;
    },
    [props.allowedFileTypes]
  );

  const buttonProps = useMemo(
    () => ({
      theme: props.theme,
      onClick: () => {
        const hiddenInput = document.querySelector(
          `#${id} input`
        ) as HTMLElement;
        if (hiddenInput) {
          hiddenInput.click();
        }
      },
      disabled: props.disabled,
      leftIcon: props.leftIcon,
      w100: props.w100,
      ...props.buttonProps,
    }),
    [
      id,
      props.buttonProps,
      props.disabled,
      props.leftIcon,
      props.theme,
      props.w100,
    ]
  );

  return (
    <div
      className={getMergedClassNames([rootClassName, props.w100 ? 'w100' : ''])}
      id={id}
    >
      <input
        hidden
        type='file'
        accept={
          props.allowedFileTypes
            ? Array(props.allowedFileTypes).join(', ')
            : undefined
        }
        tabIndex={-1}
        onChange={(evt) => {
          const files = evt.target.files || [];
          if (
            Array.from(files).some(
              (file) =>
                props.allowedFileTypes &&
                props.allowedFileTypes.length &&
                !isAllowedFileType(file.name) &&
                props.onTypeNotAllowed
            )
          ) {
            props.onTypeNotAllowed?.();
          }
          const filteredFiles = Array.from(files).filter(
            (file) =>
              !props.allowedFileTypes ||
              !props.allowedFileTypes.length ||
              isAllowedFileType(file.name)
          );
          if (props.multiple) {
            props.onChange?.(filteredFiles);
          } else {
            props.onChange?.(filteredFiles[0]);
          }
          const hiddenInput = document.querySelector(
            `#${id} input`
          ) as HTMLInputElement;
          if (hiddenInput) {
            hiddenInput.value = '';
          }
        }}
        multiple={props.multiple}
        {...(props.inputProps ?? {})}
      />
      <Button {...buttonProps}>{props.label}</Button>
    </div>
  );
}

ButtonFileUpload.defaultProps = {
  label: 'Escolher arquivo',
  theme: ButtonThemes.Primary,
  allowedFileTypes: [],
  multiple: false,
  disabled: false,
  buttonProps: {},
  w100: false,
};

export default memo(ButtonFileUpload);
